if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode)
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant
              }
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const parsedUrl = new URL(url, window.location.origin);

        if (variantId) {
          parsedUrl.searchParams.set('variant', variantId);
        } else {
          parsedUrl.searchParams.delete('variant');
        }

        window.history.replaceState({}, '', parsedUrl);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId
          }
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML
          }
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}

/* codex-ko-common-i18n-v1 */
(() => {
  if (!location.pathname.startsWith('/ko/')) return;
  const replacements = new Map([
    ['This product works best with real-person 1V1 video selection. Use the video service to inspect styles, quality, stock, packaging, and order details before confirming.', '이 상품은 실제 담당자와 1:1 영상으로 확인한 뒤 구매하는 데 가장 적합합니다. 영상 상담에서 스타일, 품질, 재고, 포장, 주문 세부 사항을 확인해 보세요.'],
    ['Choose the right next step', '다음 구매 단계를 선택하세요'],
    ['These service pages help you move from browsing one product to a complete buying plan.', '아래 서비스 페이지는 상품 탐색에서 완성된 구매 계획까지 이어지도록 도와줍니다.'],
    ['Minimum order value', '최소 주문 금액'],
    ['Orders need to meet the site minimum order value of $100 USD before checkout. Shipping fees are calculated separately.', '결제 전 사이트 최소 주문 금액 $100 USD을 충족해야 합니다. 배송비는 별도로 계산됩니다.'],
    ['Product MOQ', '상품 MOQ'],
    ['MOQ varies by item and product type. When the bulk variant selector is available, use it to choose size, style, and quantity more efficiently.', 'MOQ는 상품과 유형에 따라 다릅니다. 대량 옵션 선택기가 제공될 때는 사이즈, 스타일, 수량을 더 효율적으로 선택할 수 있습니다.'],
    ['Processing and delivery', '처리 및 배송'],
    ['Ready-stock orders usually process in about 3-5 business days. Custom products need extra production time before shipping.', '재고 상품은 보통 영업일 기준 약 3-5일 안에 처리됩니다. 맞춤 상품은 배송 전 추가 제작 시간이 필요합니다.'],
    ['Quality support', '품질 지원'],
    ['If a product has a manufacturing or package quality issue, contact us with clear photos and order details so our team can help responsibly.', '상품 제작 또는 포장 품질 문제가 있는 경우 선명한 사진과 주문 정보를 보내 주세요. 담당 팀이 책임 있게 확인하고 도와드립니다.'],
    ['Catalog hub', '카탈로그 허브'],
    ['Choose by product family, craft, MOQ, style direction, and business buying goal.', '상품군, 제작 방식, MOQ, 스타일 방향, 비즈니스 구매 목표에 따라 더 빠르게 선택하세요.'],
    ['Browse faster', '빠른 탐색'],
    ['Video 1V1 selection', '1:1 영상 셀렉션'],
    ['See real products, compare finishes, inspect packaging, and ask questions before buying.', '구매 전 실제 상품을 보고 마감, 포장, 재고를 비교하며 궁금한 점을 확인하세요.'],
    ['Real product view', '실물 확인'],
    ['Wholesale planning', '도매 구매 계획'],
    ['Plan MOQ, product mix, restock rhythm, budget, and repeat order path.', 'MOQ, 상품 구성, 재입고 리듬, 예산, 반복 주문 경로를 계획하세요.'],
    ['Bulk buying', '대량 구매'],
    ['Private label packaging', '프라이빗 라벨 포장'],
    ['Add logo stickers, cards, boxes, or complete packaging sets for brand presentation.', '브랜드 연출을 위해 로고 스티커, 카드, 박스 또는 완성 포장 세트를 추가하세요.'],
    ['Brand ready', '브랜드 준비'],
    ['Business inquiry', '비즈니스 문의'],
    ['Business Inquiry', '비즈니스 문의'],
    ['Send product goals, quantity, market, packaging needs, or support questions to our team.', '상품 목표, 수량, 시장, 포장 요구 또는 지원 질문을 팀에 보내 주세요.'],
    ['Ask 365nails', '365nails에 문의'],
    ['Shipping and tracking', '배송 및 추적'],
    ['Shipping & Tracking', '배송 및 추적'],
    ['Understand processing time, delivery timing, tracking, and logistics support.', '처리 시간, 배송 일정, 추적, 물류 지원을 확인하세요.'],
    ['Order support', '주문 지원'],
    ['Product buying FAQ', '상품 구매 FAQ'],
    ['Answers to common questions before checkout.', '결제 전 자주 묻는 질문입니다.'],
    ['Why do I see a minimum order value?', '왜 최소 주문 금액이 있나요?'],
    ['365nails is built for wholesale and business buying, so the cart needs to meet the minimum order value before checkout. This helps keep pricing, packing, and international fulfillment workable.', '365nails는 도매 및 비즈니스 구매에 맞춰 운영되므로 결제 전 장바구니가 최소 주문 금액을 충족해야 합니다. 이는 가격, 포장, 국제 배송 운영을 안정적으로 유지하는 데 도움이 됩니다.'],
    ['Does every product have the same MOQ?', '모든 상품의 MOQ가 같나요?'],
    ['No. MOQ depends on product type, stock status, customization requirements, and packaging needs. Check the product options and collection filters, or ask our team before placing a mixed order.', '아니요. MOQ는 상품 유형, 재고 상태, 맞춤 요구, 포장 필요에 따라 달라집니다. 상품 옵션과 컬렉션 필터를 확인하거나 혼합 주문 전 팀에 문의해 주세요.'],
    ['Can I confirm product details before buying in bulk?', '대량 구매 전 상품 세부 정보를 확인할 수 있나요?'],
    ['Yes. Use Video 1V1 selection when you need to see real color, thickness, finish, shape, packaging, stock, or substitutions before confirming a wholesale order.', '네. 실제 색상, 두께, 마감, 형태, 포장, 재고 또는 대체 가능 여부를 확인해야 할 때는 1:1 영상 셀렉션을 이용하세요.'],
    ['What should I do if there is a quality issue?', '품질 문제가 있으면 어떻게 해야 하나요?'],
    ['Contact 365nails with your order number, clear photos, and a description of the issue. For manufacturing or package quality problems, our team will review the case and help with a responsible solution.', '주문 번호, 선명한 사진, 문제 설명을 365nails에 보내 주세요. 제작 또는 포장 품질 문제의 경우 팀이 확인 후 책임 있는 해결을 도와드립니다.'],
    ['Need a buying plan?', '구매 계획이 필요하신가요?'],
    ['Send us your product goal, quantity, target market, and packaging needs.', '상품 목표, 수량, 목표 시장, 포장 요구를 보내 주세요.'],
    ['Ask About This Product', '이 상품 문의하기'],
    ['Read Buying Guides', '구매 가이드 보기'],
    ['Buyer support shortcuts', '구매자 지원 바로가기'],
    ['Use these paths when you need product selection, buying guides, shipping support, or a direct business inquiry.', '상품 선택, 구매 가이드, 배송 지원 또는 비즈니스 문의가 필요할 때 아래 경로를 이용하세요.'],
    ['Catalog Hub', '카탈로그 허브'],
    ['Guide Center', '가이드 센터'],
    ['Blog Hub', '블로그 허브'],
    ['Ask on WhatsApp', 'WhatsApp으로 문의'],
  ]);
  const roots = ['.product-conversion-system', '.footer-support-shortcuts'];
  function swapText(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      const value = node.nodeValue.trim();
      if (replacements.has(value)) node.nodeValue = node.nodeValue.replace(value, replacements.get(value));
    }
    for (const el of root.querySelectorAll('[aria-label]')) {
      const value = el.getAttribute('aria-label');
      if (replacements.has(value)) el.setAttribute('aria-label', replacements.get(value));
    }
  }
  function run() { roots.flatMap((selector) => [...document.querySelectorAll(selector)]).forEach(swapText); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true }); else run();
  setTimeout(run, 600);
  setTimeout(run, 1800);
})();
/* codex-ko-common-i18n-v1-end */
